/**
 * 教师端「传输条」：把开始/暂停/下一轮/揭示/重置/导出收进一条固定控制带。
 *
 * 设计目标（见 docs/design/ui-layouts/wf-04/09）：
 * - 顶部固定，不随页面滚动；比赛中的主操作永远在手边；
 * - 播放/暂停是主按钮，其余按危险级别降权；
 * - 点击/长按有物理反馈：按下缩进、松开弹簧回弹（CSS cubic-bezier）。
 *
 * 数据与可用性仍严格由 state.phase / stageIndex 决定；testid 与旧版一致，
 * 避免 E2E / 单测因布局重排而失效。
 */
import type { MatchPhase } from '@zongheng/core-schema';
import { useArena } from '../../state/ArenaContext.tsx';
import { MATCH_PHASE_LABEL, STAGE_PHASE_LABEL, formatClockMs } from '../../export/report.ts';

function phaseTone(phase: MatchPhase): string {
  if (phase === 'RUNNING') return 'text-emerald-300';
  if (phase === 'PAUSED') return 'text-amber-300';
  if (phase === 'FINISHED') return 'text-indigo-300';
  return 'text-slate-400';
}

function PlayIcon({ paused }: { paused: boolean }) {
  return paused ? (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M4 2.8c0-.5.55-.8.97-.55l8.1 5.2c.4.26.4.84 0 1.1l-8.1 5.2A.64.64 0 0 1 4 13.2V2.8Z" />
    </svg>
  ) : (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <rect x="4" y="3" width="3" height="10" rx="1" />
      <rect x="9" y="3" width="3" height="10" rx="1" />
    </svg>
  );
}

function NextIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M4 2.8c0-.5.55-.8.97-.55l7 4.45a.64.64 0 0 1 0 1.1l-7 4.45A.64.64 0 0 1 4 11.7V2.8Z" />
      <rect x="12" y="2.8" width="2" height="10.4" rx="1" />
    </svg>
  );
}

function RevealIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      aria-hidden="true"
    >
      <path d="M1.8 8s2.3-4.4 6.2-4.4S14.2 8 14.2 8s-2.3 4.4-6.2 4.4S1.8 8 1.8 8Z" />
      <circle cx="8" cy="8" r="1.8" />
    </svg>
  );
}

function ExportIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      aria-hidden="true"
    >
      <path d="M8 2.2v7.2M5.2 7.2 8 10l2.8-2.8M3 11.2v1.3c0 .7.6 1.3 1.3 1.3h7.4c.7 0 1.3-.6 1.3-1.3v-1.3" />
    </svg>
  );
}

function ResetIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      aria-hidden="true"
    >
      <path d="M3.2 6.2A5 5 0 1 1 3 8.6M3.2 2.4v3.8h3.8" />
    </svg>
  );
}

export function StageControlBar({
  onExportReport,
  exporting = false,
}: {
  onExportReport: () => void;
  exporting?: boolean;
}) {
  const { state, actions } = useArena();
  const started = state.stageIndex > 0 || state.phase !== 'LOBBY';
  const canStart = state.phase === 'LOBBY' && state.stageIndex === 0;
  const canAdvance =
    state.phase === 'RUNNING' || state.phase === 'PAUSED' || state.phase === 'STAGE_ENDED';
  const canPause = state.phase === 'RUNNING' && state.stageIndex > 0;
  const canResume = state.phase === 'PAUSED';
  const canForceReveal = state.stageIndex > 0;
  const canReset =
    started || state.reveals.length > 0 || state.annotations.length > 0 || state.alerts.length > 0;
  const stage = state.stage;
  const progress = Math.min(100, Math.round((state.completedStages.length / 16) * 100));
  const paused = state.phase === 'PAUSED';

  return (
    <section data-testid="coach-control-bar" className="lens transport-bar">
      <div className="transport-meta">
        <div className="transport-meta-line">
          <span className={`transport-live-dot ${paused ? 'transport-live-dot--paused' : ''}`} />
          <span className="micro-label">MASTER TRANSPORT · 教师权威</span>
          <span className={`mono text-[10px] ${phaseTone(state.phase)}`} data-testid="coach-phase">
            {MATCH_PHASE_LABEL[state.phase] ?? state.phase}
          </span>
        </div>

        <div className="transport-meta-line">
          <span
            className="truncate text-[15px] font-semibold tracking-tight text-slate-100"
            data-testid="coach-stage-name"
          >
            {stage ? `环节 ${stage.index}/16 · ${stage.name}` : '尚未开始'}
          </span>
          <span className="transport-kbd">SPACE 暂停/恢复 · N 下一轮 · R 揭示</span>
        </div>

        <div className="transport-meta-line text-[10.5px] text-slate-500">
          <span data-testid="coach-stage-phase">
            {STAGE_PHASE_LABEL[state.stagePhase] ?? state.stagePhase}
          </span>
          <span className="text-white/15">/</span>
          <span>
            读卡{' '}
            <b className="mono font-medium text-amber-200/80" data-testid="coach-prep-countdown">
              {state.stagePhase === 'PREP' ? formatClockMs(state.prepRemainingMs) : '—'}
            </b>
          </span>
          <span className="text-white/15">/</span>
          <span data-testid="coach-running-side" className="text-slate-400">
            {state.runningSide === 'AFF'
              ? '正方计时中'
              : state.runningSide === 'NEG'
                ? '反方计时中'
                : '未开表'}
          </span>
          <span className="text-white/15">/</span>
          <span>{state.completedStages.length}/16 已完成</span>
        </div>

        <div className="transport-progress" aria-hidden="true">
          <span style={{ width: `${progress}%` }} />
        </div>
      </div>

      <div className="transport-controls">
        <button
          type="button"
          data-testid="start-match"
          className="transport-btn transport-btn--primary"
          disabled={!canStart}
          style={{ display: canStart ? undefined : 'none' }}
          onClick={() => actions.startMatch()}
        >
          <PlayIcon paused={false} />
          开始比赛
        </button>

        <button
          type="button"
          data-testid="pause-match"
          className={`transport-btn transport-btn--icon transport-play ${
            canPause && !paused ? 'transport-btn--primary' : ''
          }`}
          disabled={!canPause}
          style={{ display: canPause ? undefined : 'none' }}
          onClick={() => actions.pauseMatch()}
          aria-label="暂停"
          title="暂停（长按有按压反馈）"
        >
          <PlayIcon paused={false} />
        </button>
        <button
          type="button"
          data-testid="resume-match"
          className={`transport-btn transport-btn--icon transport-play ${
            canResume ? 'transport-btn--primary' : ''
          }`}
          disabled={!canResume}
          style={{ display: canResume ? undefined : 'none' }}
          onClick={() => actions.resumeMatch()}
          aria-label="恢复"
          title="恢复"
        >
          <PlayIcon paused />
        </button>

        <button
          type="button"
          data-testid="advance-stage"
          className="transport-btn"
          disabled={!canAdvance}
          onClick={() => actions.advanceStage()}
          title="结束本环；再次点击进入下一轮"
        >
          <NextIcon />
          下一轮 / 下一环节
        </button>

        <button
          type="button"
          data-testid="force-reveal"
          className="transport-btn transport-btn--danger"
          disabled={!canForceReveal}
          onClick={() => actions.forceReveal()}
          title="立即揭示当前配对双方底牌"
        >
          <RevealIcon />
          揭示
        </button>

        <button
          type="button"
          data-testid="reset-match"
          className="transport-btn"
          disabled={!canReset}
          onClick={() => actions.resetMatch()}
          title="重置本场比赛"
        >
          <ResetIcon />
          重置
        </button>

        <span className="transport-divider" />

        <button
          type="button"
          data-testid="export-report"
          className="transport-btn"
          disabled={exporting}
          onClick={onExportReport}
          title="调用教师端 exportReport 并生成 Markdown 战报预览"
        >
          <ExportIcon />
          {exporting ? '导出中…' : '导出战报'}
        </button>
      </div>
    </section>
  );
}
